import * as React from "react";
import { PolizeiBlock } from "./shared";
import { PhotoUpload } from "../PhotoUpload";
import { FONT_BODY } from "../../schadenmeldung/theme";
import { BIG_COPY, YES_NO, YES_NO_UNSURE } from "../copy";
import { type BigStepProps, type GebEinheit, toggleValue, makeEinheit } from "../types";
import {
  StepHead,
  SubHead,
  Block,
  FieldGrid,
  TextField,
  TextAreaField,
  NumberField,
  RadioGroup,
  CheckboxList,
  InfoBox,
  ContactBlock,
} from "../fields";

const chipBase: React.CSSProperties = {
  appearance: "none",
  cursor: "pointer",
  fontFamily: FONT_BODY,
  fontWeight: 600,
  fontSize: "0.9rem",
  color: "var(--sm-primary-deep)",
  background: "#fff",
  border: "1.5px solid var(--sm-card-border)",
  borderRadius: "999px",
  padding: "8px 14px",
};
const chipActive: React.CSSProperties = {
  background: "var(--sm-accent-soft)",
  borderColor: "var(--sm-accent)",
};

function EinheitenTabelle({
  value,
  onChange,
}: {
  value: GebEinheit[];
  onChange: (next: GebEinheit[]) => void;
}) {
  const C = BIG_COPY.gebWhere;
  const byEtage = (id: string) => value.find((e) => e.etage === id);
  const toggleEtage = (id: string) =>
    onChange(byEtage(id) ? value.filter((e) => e.etage !== id) : [...value, makeEinheit(id)]);
  const patchEtage = (id: string, patch: Partial<GebEinheit>) =>
    onChange(value.map((e) => (e.etage === id ? { ...e, ...patch } : e)));
  return (
    <Block>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
        {C.etagen.map((et) => {
          const active = !!byEtage(et.value);
          return (
            <button
              key={et.value}
              type="button"
              aria-pressed={active}
              onClick={() => toggleEtage(et.value)}
              style={active ? { ...chipBase, ...chipActive } : chipBase}
            >
              {et.label}
            </button>
          );
        })}
      </div>
      {value.map((e) => {
        const label = C.etagen.find((x) => x.value === e.etage)?.label ?? e.etage;
        return (
          <div
            key={e.etage}
            style={{
              border: "1px solid var(--sm-card-border)",
              borderRadius: "12px",
              padding: "14px",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            <strong style={{ color: "var(--sm-primary-deep)", fontFamily: FONT_BODY }}>{label}</strong>
            <CheckboxList label={C.lageLabel} options={C.lageOptions} values={e.lage} onToggle={(v) => patchEtage(e.etage, { lage: toggleValue(e.lage, v) })} columns />
            <RadioGroup label={C.vermietetLabel} options={YES_NO} value={e.vermietet} onChange={(v) => patchEtage(e.etage, { vermietet: v })} columns />
            <TextField label={C.zusatzLabel} value={e.zusatz} onChange={(v) => patchEtage(e.etage, { zusatz: v })} optional />
          </div>
        );
      })}
    </Block>
  );
}

export function GebWhere({ answers, update }: BigStepProps) {
  const C = BIG_COPY.gebWhere;
  const anyVermietet = answers.gebEinheiten.some((e) => e.vermietet === "ja");
  return (
    <div>
      <StepHead eyebrow={C.eyebrow} title={C.title} hint={C.hint} />
      <Block>
        <FieldGrid>
          <TextField label={C.plz} value={answers.gebPlz} onChange={(v) => update({ gebPlz: v })} inputMode="numeric" />
          <TextField label={C.ort} value={answers.gebOrt} onChange={(v) => update({ gebOrt: v })} />
        </FieldGrid>
        <TextField label={C.strasse} value={answers.gebStrasse} onChange={(v) => update({ gebStrasse: v })} />
        <FieldGrid>
          <TextField label={C.objektNr} value={answers.gebObjektNr} onChange={(v) => update({ gebObjektNr: v })} optional />
          <TextField label={C.wohneinheit} value={answers.gebWohneinheit} onChange={(v) => update({ gebWohneinheit: v })} optional />
        </FieldGrid>
      </Block>

      <SubHead title={C.einheitTitle} />
      <Block>
        <p style={{ margin: 0, color: "#6c6171", fontSize: "0.86rem" }}>{C.einheitHint}</p>
        <EinheitenTabelle value={answers.gebEinheiten} onChange={(next) => update({ gebEinheiten: next })} />
        <TextField label={C.sonstigeAngaben} value={answers.gebSonstigeAngaben} onChange={(v) => update({ gebSonstigeAngaben: v })} optional />
      </Block>

      <SubHead title="Vermietung & Mieter" />
      <Block>
        {!anyVermietet && (
          <p style={{ margin: 0, color: "#6c6171", fontSize: "0.86rem" }}>
            Markieren Sie oben eine Einheit als „vermietet", um Mieterangaben zu erfassen.
          </p>
        )}
        {anyVermietet && (
          <>
            <InfoBox>{C.mieterInfo}</InfoBox>
            <ContactBlock value={answers.gebMieterContact} onChange={(patch) => update({ gebMieterContact: { ...answers.gebMieterContact, ...patch } })} show={{ emailOptional: true }} />
            <RadioGroup label={C.mieterHausrat} options={YES_NO} value={answers.gebMieterHausrat} onChange={(v) => update({ gebMieterHausrat: v })} columns />
            {answers.gebMieterHausrat === "ja" && (
              <FieldGrid>
                <TextField label={C.mieterVersicherer} value={answers.gebMieterVersicherer} onChange={(v) => update({ gebMieterVersicherer: v })} optional />
                <TextField label={C.mieterVsnr} value={answers.gebMieterVsnr} onChange={(v) => update({ gebMieterVsnr: v })} optional />
              </FieldGrid>
            )}
            <CheckboxList label={C.beschaedigtLabel} options={C.beschaedigtOptions} values={answers.gebBeschaedigt} onToggle={(v) => update({ gebBeschaedigt: toggleValue(answers.gebBeschaedigt, v) })} columns />
          </>
        )}
      </Block>
    </div>
  );
}

export function GebArt({ answers, update }: BigStepProps) {
  const C = BIG_COPY.gebArt;
  const unter = answers.gebSchadenart ? C.untergefahren[answers.gebSchadenart] : null;
  return (
    <div>
      <StepHead eyebrow={C.eyebrow} title={C.title} hint={C.hint} />
      <Block>
        <RadioGroup
          options={C.options}
          value={answers.gebSchadenart}
          onChange={(v) => update({ gebSchadenart: v, gebUntergefahr: null, gebUntergefahrDetail: "" })}
          columns
        />
        {answers.gebSchadenart === "sonstige" && (
          <InfoBox summary="Was sind weitere Elementargefahren?">{C.info}</InfoBox>
        )}
      </Block>

      {unter && (
        <>
          <SubHead title={C.untergefahrLabel} />
          <Block>
            <RadioGroup options={unter} value={answers.gebUntergefahr} onChange={(v) => update({ gebUntergefahr: v })} columns />
            {answers.gebUntergefahr === "sonstiges" && (
              <TextField label={C.untergefahrDetail} value={answers.gebUntergefahrDetail} onChange={(v) => update({ gebUntergefahrDetail: v })} />
            )}
          </Block>
        </>
      )}
    </div>
  );
}

export function GebHergang({ answers, update }: BigStepProps) {
  const C = BIG_COPY.gebHergang;
  return (
    <div>
      <StepHead eyebrow={C.eyebrow} title={C.title} hint={C.hint} />
      <Block>
        <TextAreaField label={C.hergang} value={answers.gebHergang} onChange={(v) => update({ gebHergang: v })} />
        <TextField label={C.schadenstelle} value={answers.gebSchadenstelle} onChange={(v) => update({ gebSchadenstelle: v })} />
        <PhotoUpload hint="Fotos können Sie auch am Ende der Schadenmeldung noch hochladen." />
        <RadioGroup label={C.ursacheLabel} options={C.ursacheOptions} value={answers.gebUrsache} onChange={(v) => update({ gebUrsache: v })} columns />
        <TextField label={C.folgeschaeden} value={answers.gebFolgeschaeden} onChange={(v) => update({ gebFolgeschaeden: v })} optional />
      </Block>

      <SubHead title={C.umfangTitle} />
      <Block>
        <TextAreaField label={C.betroffeneTeile} value={answers.gebBetroffeneTeile} onChange={(v) => update({ gebBetroffeneTeile: v })} />
        <NumberField label={C.schadenhoehe} value={answers.gebSchadenhoehe} onChange={(v) => update({ gebSchadenhoehe: v })} suffix="EUR" optional />
        <RadioGroup label={C.nutzung} options={YES_NO} value={answers.gebNutzungMoeglich} onChange={(v) => update({ gebNutzungMoeglich: v })} columns />
        {answers.gebNutzungMoeglich === "nein" && (
          <TextAreaField label={C.nutzungDetail} value={answers.gebNutzungDetail} onChange={(v) => update({ gebNutzungDetail: v })} />
        )}
        <InfoBox summary="Hinweis zur Schadenhöhe">{C.hoeheInfo}</InfoBox>
      </Block>
    </div>
  );
}

export function GebMassnahmen({ answers, update }: BigStepProps) {
  const C = BIG_COPY.gebMassnahmen;
  return (
    <div>
      <StepHead eyebrow={C.eyebrow} title={C.title} hint={C.hint} />
      <Block>
        <RadioGroup label={C.beauftragt} options={YES_NO} value={answers.gebMassnahmeBeauftragt} onChange={(v) => update({ gebMassnahmeBeauftragt: v })} columns />
        {answers.gebMassnahmeBeauftragt === "ja" && (
          <>
            <TextAreaField label={C.was} value={answers.gebMassnahmeWas} onChange={(v) => update({ gebMassnahmeWas: v })} />
            <FieldGrid>
              <TextField label={C.firma} value={answers.gebFirma} onChange={(v) => update({ gebFirma: v })} optional />
              <TextField label={C.auftragsnummer} value={answers.gebAuftragsnummer} onChange={(v) => update({ gebAuftragsnummer: v })} optional />
            </FieldGrid>
          </>
        )}
        <FieldGrid>
          <RadioGroup label={C.kostenvoranschlag} options={YES_NO} value={answers.gebKostenvoranschlag} onChange={(v) => update({ gebKostenvoranschlag: v })} columns />
          <RadioGroup label={C.rechnungen} options={YES_NO} value={answers.gebRechnungen} onChange={(v) => update({ gebRechnungen: v })} columns />
        </FieldGrid>
      </Block>

      <SubHead title={C.sanierungTitle} />
      <Block>
        <RadioGroup label={C.sanierungLabel} options={YES_NO_UNSURE} value={answers.gebSanierung} onChange={(v) => update({ gebSanierung: v })} columns />
        <InfoBox>{C.sanierungInfo}</InfoBox>
      </Block>
    </div>
  );
}

export function GebVerursacher({ answers, update }: BigStepProps) {
  const C = BIG_COPY.gebVerursacher;
  return (
    <div>
      <StepHead eyebrow={C.eyebrow} title={C.title} hint={C.hint} />
      <Block>
        <RadioGroup label={C.label} options={YES_NO_UNSURE} value={answers.gebVerursacher} onChange={(v) => update({ gebVerursacher: v })} columns />
        <InfoBox>{C.info}</InfoBox>
        {answers.gebVerursacher === "ja" && (
          <>
            <ContactBlock value={answers.gebVerursacherContact} onChange={(patch) => update({ gebVerursacherContact: { ...answers.gebVerursacherContact, ...patch } })} show={{ emailOptional: true }} />
            <RadioGroup label={C.haftpflicht} options={YES_NO} value={answers.gebVerursacherHaftpflicht} onChange={(v) => update({ gebVerursacherHaftpflicht: v })} columns />
            {answers.gebVerursacherHaftpflicht === "ja" && (
              <FieldGrid>
                <TextField label={C.versicherer} value={answers.gebVerursacherVersicherer} onChange={(v) => update({ gebVerursacherVersicherer: v })} optional />
                <TextField label={C.vsnr} value={answers.gebVerursacherVsnr} onChange={(v) => update({ gebVerursacherVsnr: v })} optional />
              </FieldGrid>
            )}
          </>
        )}
      </Block>

      <PolizeiBlock
        polizei={answers.gebPolizei}
        aktenzeichen={answers.gebPolizeiAktenzeichen}
        dienststelle={answers.gebPolizeiDienststelle}
        onChange={(p) =>
          update({
            ...(p.polizei !== undefined ? { gebPolizei: p.polizei } : {}),
            ...(p.aktenzeichen !== undefined ? { gebPolizeiAktenzeichen: p.aktenzeichen } : {}),
            ...(p.dienststelle !== undefined ? { gebPolizeiDienststelle: p.dienststelle } : {}),
          })
        }
        showInfo
      />

      <SubHead title={C.weitereTitle} />
      <Block>
        <RadioGroup label={C.weitereLabel} options={YES_NO} value={answers.gebWeitere} onChange={(v) => update({ gebWeitere: v })} columns />
        {answers.gebWeitere === "ja" && (
          <>
            <ContactBlock value={answers.gebWeitereContact} onChange={(patch) => update({ gebWeitereContact: { ...answers.gebWeitereContact, ...patch } })} show={{ emailOptional: true }} />
            <TextAreaField label={C.weitereArt} value={answers.gebWeitereArt} onChange={(v) => update({ gebWeitereArt: v })} />
          </>
        )}
      </Block>
    </div>
  );
}

export function GebAbrechnung({ answers, update }: BigStepProps) {
  const C = BIG_COPY.gebAbrechnung;
  return (
    <div>
      <StepHead eyebrow={C.eyebrow} title={C.title} hint={C.hint} />
      <Block>
        <RadioGroup label={C.vorsteuerLabel} options={C.vorsteuerOptions} value={answers.gebVorsteuer} onChange={(v) => update({ gebVorsteuer: v })} columns />
        {answers.gebVorsteuer === "ja" && (
          <NumberField label={C.vorsteuerAnteil} value={answers.gebVorsteuerAnteil} onChange={(v) => update({ gebVorsteuerAnteil: v })} suffix="%" maxLength={3} />
        )}
      </Block>

      <SubHead title="Schadenzahlung" />
      <Block>
        <RadioGroup label={C.zahlungLabel} options={C.zahlungOptions} value={answers.gebZahlungEmpfaenger} onChange={(v) => update({ gebZahlungEmpfaenger: v })} columns />
        <TextField label={C.zahlungName} value={answers.gebZahlungName} onChange={(v) => update({ gebZahlungName: v })} optional />
        <FieldGrid>
          <TextField label={C.iban} value={answers.gebIban} onChange={(v) => update({ gebIban: v })} optional />
          <TextField label={C.bic} value={answers.gebBic} onChange={(v) => update({ gebBic: v })} optional />
        </FieldGrid>
      </Block>

      <SubHead title={C.zusatzTitle} />
      <Block>
        <TextAreaField label={C.zusatzLabel} value={answers.gebZusatz} onChange={(v) => update({ gebZusatz: v })} placeholder={C.zusatzPlaceholder} />
      </Block>
    </div>
  );
}
